/* eslint-disable complexity */
import { useCallback, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { get, isEmpty, isNull } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  CHAT,
  CHAT_ACTIONS,
  endChatNow,
  getIsChatAvailable,
  isCustomerServiceOpen,
  resetChatState,
  resetConfigState,
  restoreChatSession,
  restoreConfigSession,
  setModalStateClosed,
} from '@dpdgroupuk/cc-chat-fe';

import ActionButton from '../../../components/ActionButton';
import EmptySearchResults from '../../../components/EmptySearchResults';
import OrderDetails from '../../../components/OrderDetails';
import { ParcelHelpers, Validators } from '../../../helpers';
import { resetFields } from '../../../helpers/form';
import {
  Error,
  ERROR_BOUNDARY_TYPES,
  useErrorBoundaryConfigurations,
} from '../../../features/ErrorBoundary';
import { useOverlay } from '../../../features/Overlay';
import { useCustomSnackbar } from '../../../features/CustomSnackbar';
import { chatApis, parcelsApis } from '../../../apis';
import { getDayAndMonth } from '../../../utils/dateAndTime';
import { FORM, STRINGS } from '../../../constants';
import { CHAT_CONFIG } from '../../../constants/env';
import ConfigSlice from '../../../redux/configSlice';
import {
  CONTACT_US_TYPE,
  FIELDS_TO_RESET,
  HELP_TYPES,
  SEARCH_FIELD_SET,
} from '../constants';
import { getClaimData, isClaimInvalid, normalizeClaimData } from '../helpers';
import ClaimTypeSelect from './ClaimTypeSelect';
import SearchParcelFields from './SearchParcelFields';
import ContactDetails from './ContactDetails';
import ClaimDetails from './ClaimDetails';
import ChatUnavailable from './ChatUnavailable';
import SuccessScreen from './SuccessScreen';
import ChatModal from './ChatModal';
import ChatButton from './ChatButton';

const { requestChatDetails } = CHAT_ACTIONS;

export function DpdOnlineHelpFlow({ chatButtonContainer }) {
  const overlay = useOverlay();
  const { showError, showSuccess } = useCustomSnackbar();
  const getErrorBoundaryConfig = useErrorBoundaryConfigurations();
  const daysLimitForClaimProcessing = useSelector(
    ConfigSlice.selectors.getDaysLimitForClaimProcessing
  );
  const dispatch = useDispatch();
  const modalState = useSelector(state => state.chat?.modalState);

  const [submitted, setSubmitted] = useState(false);
  const [parcel, setParcel] = useState({});
  const [chatConfig, setChatConfig] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [activeFieldSet, setActiveFieldSet] = useState(null);
  const [searchedValues, setSearchedValues] = useState({
    parcelNumber: null,
    postcodeOrOrderId: null,
    collectionReference: null,
    collectionPostcode: null,
  });

  const chatButtonRef = useRef(null);
  const deliveredDate = get(parcel, 'deliveryDetails.podDetails.podDate');

  const isParcelIssueSelectedFromValues = values =>
    get(values, FORM.HELP_FIELDS.HELP_TYPE.KEY) === HELP_TYPES.ISSUE;

  const createCase = useCallback(
    async (values, errorMessage, onSuccess) => {
      try {
        overlay.show();
        const normalizedValues = normalizeClaimData(values);
        const result = await parcelsApis.createParcelClaim(normalizedValues);

        showSuccess({ message: STRINGS.CASE_CREATED_SUCCESS });
        onSuccess(result.data.id);
      } catch (error) {
        showError({ message: errorMessage });
      } finally {
        overlay.hide();
      }
    },
    [overlay, showError, showSuccess]
  );

  const onFormSubmit = useCallback(
    async values => {
      const isParcelIssueSelected = isParcelIssueSelectedFromValues(values);

      if (isParcelIssueSelected && !isEmpty(parcel)) {
        const claimData = getClaimData(values, parcel);
        createCase(claimData, STRINGS.SEND_CLAIM_ERROR, () =>
          setSubmitted(true)
        );
      } else {
        setSubmitted(true);
      }
    },
    [createCase, parcel]
  );

  const onChatOptionClick = useCallback(async () => {
    try {
      overlay.show();

      const chatConfigResponse = await chatApis.fetchChatConfig();
      const mergedChatConfig = {
        ...CHAT_CONFIG,
        ...chatConfigResponse,
      };

      setActiveFieldSet(null);
      setChatConfig(mergedChatConfig);
    } catch (error) {
      showError({ message: STRINGS.CHAT_CONFIG_ERROR });
    } finally {
      overlay.hide();
    }
  }, [overlay, showError]);

  const onParcelFound = useCallback(
    async (foundParcel, values) => {
      const isChatWithUsSelected =
        get(values, FORM.HELP_FIELDS.CONTACT_US_TYPE.KEY) ===
        CONTACT_US_TYPE.CHAT;

      setSearchedValues({
        parcelNumber: get(values, FORM.HELP_FIELDS.PARCEL_NUMBER.KEY),
        postcodeOrOrderId: get(
          values,
          FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY
        ),
        collectionReference: get(
          values,
          FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY
        ),
        collectionPostcode: get(
          values,
          FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY
        ),
      });

      if (!isEmpty(foundParcel) && isChatWithUsSelected && chatConfig) {
        const isCollection = !!get(
          values,
          FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY
        );

        dispatch(resetChatState());

        const postcodeOrOrderId = get(
          values,
          FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY
        );
        const { postcode: inputPostcode, orderId } =
          ParcelHelpers.getPostcodeOrOrderId(postcodeOrOrderId);

        const queryParams = {
          parcelCode: foundParcel.parcelCode,
        };

        const deliveryPostcode = get(
          foundParcel,
          'deliveryDetails.address.postcode'
        );

        const postcode = deliveryPostcode || inputPostcode;

        if (postcode) {
          queryParams.postcode = postcode;
        }

        if (foundParcel.orderId || orderId) {
          queryParams.orderId = foundParcel.orderId || orderId;
        }

        const chatConfigResponse = await chatApis.fetchChatConfig(queryParams);

        const updatedChatConfig = {
          ...chatConfig,
          ...chatConfigResponse,
          parcelNumber: foundParcel.parcelNumber,
          parcelCode: foundParcel.parcelCode,
          postcode,
          orderId: foundParcel.orderId || orderId,
          collectionReference: isCollection
            ? get(values, FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY)
            : null,
          collectionPostcode: isCollection
            ? get(values, FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY)
            : null,
          botAvailable: isCollection ? false : chatConfigResponse?.botAvailable,
        };

        setChatConfig(updatedChatConfig);

        dispatch(
          requestChatDetails(
            foundParcel.parcelNumber,
            foundParcel.parcelCode,
            foundParcel.orderId || orderId,
            postcode,
            isCollection
              ? get(values, FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY)
              : null,
            isCollection
              ? get(values, FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY)
              : null,
            isCollection ? false : null
          )
        );

        if (getIsChatAvailable(updatedChatConfig)) {
          setIsModalOpen(true);
          setIsChatMinimized(false);
        }
      }
    },
    [chatConfig, dispatch]
  );

  const resetForm = useCallback(form => {
    form.restart();
    form.change(FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY, '');
    form.change(FORM.HELP_KEYS.CONTACT_DETAILS, undefined);
    setParcel({});
    setChatConfig(null);
    setSearchedValues({
      parcelNumber: null,
      postcodeOrOrderId: null,
      collectionReference: null,
      collectionPostcode: null,
    });
  }, []);

  const minimizeChatModal = useCallback(() => {
    setIsChatMinimized(true);
  }, []);

  const handleChatEnd = useCallback(
    closeModal => {
      if (chatConfig) {
        const {
          openTime,
          closeTime,
          businessUnit,
          botAvailable,
          advisorAvailable,
        } = chatConfig;
        setChatConfig({
          botAvailable,
          advisorAvailable,
          openTime,
          closeTime,
          businessUnit,
          parcelCode: null,
          parcelNumber: null,
          orderId: null,
          postCode: null,
          collectionReference: null,
          collectionPostcode: null,
        });
      }

      if (closeModal) {
        setSearchedValues({
          parcelNumber: null,
          postcodeOrOrderId: null,
          collectionReference: null,
          collectionPostcode: null,
        });
        setActiveFieldSet(null);
        setIsModalOpen(false);
        dispatch(setModalStateClosed());
      }
    },
    [dispatch, chatConfig]
  );

  const clearSearchedValues = useCallback(() => {
    setSearchedValues({
      parcelNumber: null,
      postcodeOrOrderId: null,
      collectionReference: null,
      collectionPostcode: null,
    });
    dispatch(endChatNow({ ratingEnabled: false }));
    dispatch(setModalStateClosed());
    dispatch(resetConfigState());
    setIsModalOpen(false);
  }, [dispatch]);

  const handleBeforeSearch = useCallback(async () => {
    dispatch(endChatNow({ ratingEnabled: false }));
    dispatch(setModalStateClosed());
    dispatch(resetConfigState());
    setIsModalOpen(false);
  }, [dispatch]);

  if (
    !isEmpty(parcel) &&
    isClaimInvalid(deliveredDate, daysLimitForClaimProcessing)
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignSelf: 'center',
          flexGrow: 1,
        }}
      >
        <Error
          config={getErrorBoundaryConfig[
            ERROR_BOUNDARY_TYPES.CANNOT_PROCESS_CLAIM
          ](getDayAndMonth(deliveredDate))}
          sx={{ maxWidth: '447px', alignSelf: 'center' }}
        />
      </Box>
    );
  }

  if (submitted) {
    return <SuccessScreen />;
  }

  return (
    <>
      <Form
        onSubmit={onFormSubmit}
        validate={values =>
          Validators.validateWithJoi(values, Validators.helpParcelSchema, {
            context: {
              reasonForContact: values.reasonForContact,
              activeSearchFieldSet: activeFieldSet,
            },
          })
        }
      >
        {({ values, errors, invalid, handleSubmit, form }) => {
          const isParcelIssueSelected = isParcelIssueSelectedFromValues(values);
          const isContactUsSelected =
            get(values, FORM.HELP_FIELDS.HELP_TYPE.KEY) === HELP_TYPES.CHAT;
          const isChatWithUsSelected =
            get(values, FORM.HELP_FIELDS.CONTACT_US_TYPE.KEY) ===
            CONTACT_US_TYPE.CHAT;
          const collectionReference = get(
            values,
            FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY
          );
          const isChatSelected =
            getIsChatAvailable(chatConfig) && isChatWithUsSelected;
          const showSearchParcelFields =
            isParcelIssueSelected || (isContactUsSelected && chatConfig);
          const showChatButton = Boolean(
            isChatSelected &&
              (searchedValues.parcelNumber ||
                searchedValues.collectionReference) &&
              getIsChatAvailable(chatConfig)
          );

          const contactDetails = get(values, FORM.HELP_KEYS.CONTACT_DETAILS);
          const hasContactDetails =
            contactDetails?.name && contactDetails?.email;
          const isContactDetailsValid = !errors.contactDetails;
          const issueDetailsAvailable =
            hasContactDetails && isContactDetailsValid;

          return (
            <form onSubmit={handleSubmit}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant='h3' sx={{ mb: 1 }}>
                  {STRINGS.CHOOSE_ONE_OF_OPTIONS}
                </Typography>
                <Field name={FORM.HELP_FIELDS.HELP_TYPE.KEY}>
                  {({ input }) => (
                    <Grid
                      container
                      spacing={2}
                      sx={{ mt: 1, alignItems: 'stretch' }}
                    >
                      <Grid container size={{ md: 6, xs: 12 }}>
                        <ActionButton
                          id='help-contact-us'
                          selected={input.value === HELP_TYPES.CHAT}
                          title={STRINGS.CONTACT_US}
                          bodyText={STRINGS.GET_ANSWER_WITH_CHAT}
                          cardOnclick={() => {
                            resetForm(form);
                            setActiveFieldSet(null);
                            input.onChange(HELP_TYPES.CHAT);
                          }}
                          additionalInfo={false}
                        />
                      </Grid>
                      <Grid container size={{ md: 6, xs: 12 }}>
                        <ActionButton
                          id='help-parcel-issue'
                          selected={input.value === HELP_TYPES.ISSUE}
                          title={STRINGS.PARCEL_ISSUE}
                          bodyText={STRINGS.WE_CAN_HELP_PARCEL_ISSUE}
                          additionalInfo={false}
                          cardOnclick={() => {
                            resetForm(form);
                            setActiveFieldSet(SEARCH_FIELD_SET.DELIVERY);
                            input.onChange(HELP_TYPES.ISSUE);
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Field>
              </Paper>
              {isContactUsSelected ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant='h3' sx={{ mb: 1 }}>
                    {STRINGS.CHOOSE_ONE_OF_OPTIONS}
                  </Typography>
                  <Field name={FORM.HELP_FIELDS.CONTACT_US_TYPE.KEY}>
                    {({ input }) => (
                      <ActionButton
                        id='help-chat-with-us'
                        selected={input.value === CONTACT_US_TYPE.CHAT}
                        title={STRINGS.CHAT_WITH_US}
                        bodyText={STRINGS.CHAT_TO_OUR_TEAM}
                        additionalInfo={false}
                        cardOnclick={() => {
                          input.onChange(CONTACT_US_TYPE.CHAT);
                          onChatOptionClick();
                        }}
                      />
                    )}
                  </Field>
                </Paper>
              ) : null}
              {isModalOpen ? (
                <ChatModal
                  isMinimised={isChatMinimized}
                  onMinimize={minimizeChatModal}
                  onClose={handleChatEnd}
                  chatConfig={chatConfig}
                  parcelCode={parcel?.parcelCode}
                  collectionReference={
                    searchedValues.collectionReference || collectionReference
                  }
                />
              ) : null}
              {showSearchParcelFields ? (
                <Paper sx={{ p: 2, pb: 3, mb: 2 }}>
                  <Typography variant='h3' sx={{ mb: 1 }}>
                    {isContactUsSelected
                      ? STRINGS.ENTER_YOUR_TRACKING_DETAILS_BELOW
                      : STRINGS.ENTER_YOUR_PARCEL_NUMBER}
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <SearchParcelFields
                      setParcel={setParcel}
                      setActiveFieldSet={setActiveFieldSet}
                      activeFieldSet={activeFieldSet}
                      collectionReference={collectionReference}
                      isContactUsSelected={isContactUsSelected}
                      onParcelFound={foundParcel =>
                        onParcelFound(foundParcel, values)
                      }
                      onFieldChange={clearSearchedValues}
                      onBeforeSearch={handleBeforeSearch}
                      advisorAvailable={
                        chatConfig?.advisorAvailable &&
                        isCustomerServiceOpen(chatConfig)
                      }
                      errors={errors}
                    />
                  </Box>
                </Paper>
              ) : null}

              {showChatButton ? (
                <ChatButton
                  disabled={!isChatMinimized}
                  onClick={() => {
                    if (
                      modalState?.modalState === CHAT.MODAL_STATE.MINIMIZED &&
                      modalState?.sessionData
                    ) {
                      const sessionData = modalState.sessionData;

                      if (
                        modalState?.hasActiveSession &&
                        (sessionData.dfCaseId || sessionData.dfId)
                      ) {
                        dispatch(restoreConfigSession(sessionData));
                        dispatch(restoreChatSession(sessionData));
                      } else {
                        dispatch(restoreConfigSession(sessionData));
                      }

                      setIsChatMinimized(false);
                    } else {
                      const currentParcelNumber = searchedValues.parcelNumber;
                      const currentPostcode = searchedValues.postcodeOrOrderId;
                      const currentCollectionRef =
                        searchedValues.collectionReference;
                      const currentCollectionPostcode =
                        searchedValues.collectionPostcode;

                      if (currentParcelNumber || currentCollectionRef) {
                        const updatedChatConfig = {
                          ...chatConfig,
                          parcelNumber: currentParcelNumber,
                          postcode: currentPostcode,
                          collectionReference: currentCollectionRef,
                          collectionPostcode: currentCollectionPostcode,
                        };

                        dispatch(
                          requestChatDetails(
                            currentParcelNumber,
                            parcel?.parcelCode,
                            parcel?.orderId,
                            currentPostcode,
                            currentCollectionRef,
                            currentCollectionPostcode,
                            currentCollectionRef ? false : null
                          )
                        );

                        setChatConfig(updatedChatConfig);
                        setIsChatMinimized(false);
                      }
                    }
                  }}
                  container={chatButtonContainer?.current}
                  buttonRef={chatButtonRef}
                />
              ) : null}

              {isParcelIssueSelected && !isEmpty(parcel) ? (
                <>
                  <OrderDetails
                    parcel={parcel}
                    summaryTitle={STRINGS.YOUR_PARCEL_SUMMARY}
                    trackingImages={parcel?.parcelEvents[0]?.images}
                    showBackButton={false}
                    isFullView={false}
                  />
                  <ClaimTypeSelect />
                  {values.reasonForContact ? (
                    <>
                      <ContactDetails />
                      <OnChange name={FORM.HELP_FIELDS.ISSUE_TYPE.KEY}>
                        {() => {
                          resetFields(FIELDS_TO_RESET, form);
                        }}
                      </OnChange>
                      <ClaimDetails
                        issueType={values.reasonForContact}
                        isAvailable={issueDetailsAvailable}
                      />
                      <Box
                        sx={{
                          ml: 'auto',
                          width: 'fit-content',
                          pr: { xs: 2, md: 0 },
                        }}
                      >
                        <Button
                          variant='contained'
                          type='submit'
                          disabled={invalid}
                        >
                          {STRINGS.SUBMIT}
                        </Button>
                      </Box>
                    </>
                  ) : null}
                </>
              ) : null}
            </form>
          );
        }}
      </Form>
      {isNull(parcel) ? <EmptySearchResults /> : null}
      {chatConfig && !getIsChatAvailable(chatConfig) ? (
        <ChatUnavailable />
      ) : null}
    </>
  );
}
